import {
  buildBreadcrumbSchema,
  buildFaqSchema,
  buildHowToSchema,
  buildSoftwareApplicationSchema,
  toSeoProps,
  withDefaultJsonLd,
  type JsonLdExpectation,
  type SeoRouteConfig,
  type UmbrellaSeoRoutes,
} from '@saas-pages/shared/public/seo'
import {
  buildUtilityRoutePathMap,
  createUtilityRouteResolver,
  type UtilityAppRegistry,
  type UtilityStaticRouteDefinition,
} from '@saas-pages/shared/public/platform-legacy'
import type { SeoProps } from '@saas-pages/shared/public/seo'
import branding from '../../branding.config'
import { DEFAULT_LOCALE, type Locale } from '../shared/i18n/locale'
import { enTranslations } from '../shared/i18n/en'
import { ruTranslations, type Translations } from '../shared/i18n/ru'
import { approvedProductionCalendars } from '../shared/production-calendar'

type ApprovedProductionCalendarYear = (typeof approvedProductionCalendars)[number]['year']
type ProductionCalendarYearRouteKey = `production-calendar-${ApprovedProductionCalendarYear}`

export type RouteKey =
  | 'index'
  | 'calendar'
  | 'calendar-2026'
  | 'calendar-2027'
  | 'shift-calendar'
  | 'production-calendar'
  | ProductionCalendarYearRouteKey
  | 'timestamp'
  | 'add-subtract-date'
  | 'date-difference'
  | 'business-days'
  | 'age-calculator'
  | 'days-until-date'
  | 'week-number'
  | 'time-zones'
  | 'timer'
  | 'pomodoro'
  | 'stopwatch'
  | 'blog'

const productionCalendarYearRouteDefinitions = approvedProductionCalendars.map((calendar) => ({
  key: `production-calendar-${calendar.year}` as ProductionCalendarYearRouteKey,
  path: `/proizvodstvennyj-kalendar-${calendar.year}`,
  seo: {
    path: `/proizvodstvennyj-kalendar-${calendar.year}`,
    title: `Производственный календарь на ${calendar.year} год — dateify.ru`,
    description: `Производственный календарь России на ${calendar.year} год для федеральной пятидневки: ${calendar.aggregates.workingDays} рабочих и ${calendar.aggregates.nonWorkingDays} нерабочих дней, праздники и утверждённые переносы.`,
    changefreq: 'yearly' as const,
    priority: 0.9,
  },
}))

export const dateifyRouteDefinitions = [
  {
    key: 'index',
    path: '/',
    seo: {
      path: '/',
      title: 'Инструменты для работы с датами онлайн бесплатно — dateify.ru',
      description:
        'Бесплатные локальные инструменты для работы с датами и временем: Unix timestamp конвертер, разница между датами и другие инструменты для дат. Без регистрации и отправки данных на сервер.',
      changefreq: 'weekly',
      priority: 1,
    },
  },
  {
    key: 'calendar',
    path: '/kalendar',
    includeInNav: true,
    seo: {
      path: '/kalendar',
      title: 'Календарь онлайн на год и месяц — dateify.ru',
      description:
        'Бесплатный календарь онлайн на год или месяц. Начало недели с понедельника или воскресенья, номера ISO недель и версия для печати. Без праздников и переносов.',
      changefreq: 'monthly',
      priority: 1,
    },
  },
  {
    key: 'calendar-2026',
    path: '/kalendar-2026',
    seo: {
      path: '/kalendar-2026',
      title: 'Календарь 2026 на год и по месяцам — dateify.ru',
      description:
        'Календарь на 2026 год по месяцам: начало недели с понедельника или воскресенья, номера ISO недель и печать. Подсвечиваются только выходные суббота и воскресенье.',
      changefreq: 'yearly',
      priority: 0.9,
      jsonLd: {
        mode: 'required',
        schemas: [{ '@type': 'FAQPage' }],
      },
    },
  },
  {
    key: 'calendar-2027',
    path: '/kalendar-2027',
    seo: {
      path: '/kalendar-2027',
      title: 'Календарь 2027 на год и по месяцам — dateify.ru',
      description:
        'Календарь на 2027 год по месяцам: начало недели с понедельника или воскресенья, номера ISO недель и печать. Подсвечиваются только выходные суббота и воскресенье.',
      changefreq: 'yearly',
      priority: 0.9,
      jsonLd: {
        mode: 'required',
        schemas: [{ '@type': 'FAQPage' }],
      },
    },
  },
  {
    key: 'shift-calendar',
    path: '/grafik-smen',
    includeInNav: true,
    seo: {
      path: '/grafik-smen',
      title: 'График смен 2/2, 3/3 и сутки через трое — dateify.ru',
      description:
        'Составьте календарь смен на год по циклу 2/2, 3/3, сутки через трое или своему графику. Смены, выходные и часы считаются локально в браузере.',
      changefreq: 'monthly',
      priority: 0.9,
    },
  },
  {
    key: 'production-calendar',
    path: '/proizvodstvennyj-kalendar',
    seo: {
      path: '/proizvodstvennyj-kalendar',
      title: 'Производственный календарь России по годам — dateify.ru',
      description:
        'Производственный календарь РФ для пятидневки: сегодня рабочий день или нет, рабочие и нерабочие дни по годам, праздники и переносы. Источники указаны.',
      changefreq: 'monthly',
      priority: 1,
    },
  },
  ...productionCalendarYearRouteDefinitions,
  {
    key: 'timestamp',
    path: '/timestamp',
    includeInNav: true,
    seo: {
      path: '/timestamp',
      title: 'Unix timestamp конвертер онлайн — dateify.ru',
      description:
        'Конвертируйте Unix timestamp в дату и обратно. Текущее Unix время, секунды, миллисекунды, наносекунды и epoch days. Бесплатно, без регистрации, локально в браузере.',
      changefreq: 'monthly',
      priority: 1,
    },
  },
  {
    key: 'add-subtract-date',
    path: '/dobavit-dni-k-date',
    includeInNav: true,
    seo: {
      path: '/dobavit-dni-k-date',
      title: 'Прибавить дни к дате онлайн: какая дата будет — dateify.ru',
      description:
        'Прибавьте или вычтите дни, недели, месяцы и годы и сразу узнайте итоговую дату и день недели. Есть режим рабочих дней с календарём РФ. Бесплатно.',
      changefreq: 'monthly',
      priority: 0.9,
    },
  },
  {
    key: 'date-difference',
    path: '/raznica-mezhdu-datami',
    includeInNav: true,
    seo: {
      path: '/raznica-mezhdu-datami',
      title: 'Разница между датами: сколько дней и недель — dateify.ru',
      description:
        'Сколько дней и недель между двумя датами: результат сразу, полные недели с остатком, месяцы и годы. Без регистрации, считается в браузере.',
      changefreq: 'monthly',
      priority: 0.9,
    },
  },
  {
    key: 'business-days',
    path: '/rabochie-dni-mezhdu-datami',
    includeInNav: true,
    seo: {
      path: '/rabochie-dni-mezhdu-datami',
      title: 'Сколько рабочих дней между датами — калькулятор — dateify.ru',
      description:
        'Сколько рабочих дней между двумя датами: только выходные или производственный календарь РФ с праздниками и переносами. Результат сразу, бесплатно.',
      changefreq: 'monthly',
      priority: 0.9,
    },
  },
  {
    key: 'age-calculator',
    path: '/kalkulyator-vozrasta',
    includeInNav: true,
    seo: {
      path: '/kalkulyator-vozrasta',
      title: 'Калькулятор возраста онлайн — сколько лет, месяцев и дней — dateify.ru',
      description:
        'Посчитайте возраст по дате рождения: полные годы, месяцы, дни и всего прожитых дней. Бесплатный калькулятор возраста работает локально в браузере без регистрации.',
      changefreq: 'monthly',
      priority: 0.9,
    },
  },
  {
    key: 'days-until-date',
    path: '/skolko-dnej-do-daty',
    includeInNav: true,
    seo: {
      path: '/skolko-dnej-do-daty',
      title: 'Сколько дней до даты — посчитать онлайн — dateify.ru',
      description:
        'Сколько дней и недель осталось до нужной даты: выберите день и сразу увидите ответ. Покажет и сколько прошло, если дата позади. Бесплатно, без регистрации.',
      changefreq: 'monthly',
      priority: 0.9,
    },
  },
  {
    key: 'week-number',
    path: '/nomer-nedeli',
    includeInNav: true,
    seo: {
      path: '/nomer-nedeli',
      title: 'Номер недели по дате — ISO week онлайн — dateify.ru',
      description:
        'Узнайте номер ISO недели, ISO год и день недели по дате или получите диапазон дат по ISO году и номеру недели. Бесплатно, локально в браузере.',
      changefreq: 'monthly',
      priority: 0.9,
    },
  },
  {
    key: 'time-zones',
    path: '/chasovye-poyasa',
    includeInNav: true,
    seo: {
      path: '/chasovye-poyasa',
      title: 'Конвертер часовых поясов онлайн — время в разных городах — dateify.ru',
      description:
        'Сравните точное время и дату в 2–5 часовых поясах по IANA-зонам. Конвертер учитывает переходы на летнее время, работает локально в браузере и позволяет поделиться ссылкой.',
      changefreq: 'monthly',
      priority: 0.9,
    },
  },
  {
    key: 'timer',
    path: '/timer',
    includeInNav: true,
    seo: {
      path: '/timer',
      title: 'Таймер онлайн бесплатно — dateify.ru',
      description:
        'Запустите таймер с часами, минутами и секундами прямо в браузере. Бесплатно, без регистрации и установки приложения.',
      changefreq: 'monthly',
      priority: 0.8,
    },
  },
  {
    key: 'pomodoro',
    path: '/pomodoro-tajmer',
    includeInNav: true,
    seo: {
      path: '/pomodoro-tajmer',
      title: 'Помодоро таймер онлайн — 25/5 и 50/10 — dateify.ru',
      description:
        'Бесплатный Pomodoro таймер для фокусировки: интервалы 25/5, 50/10 и свои длительности. Работает локально в браузере, без регистрации.',
      changefreq: 'monthly',
      priority: 0.8,
    },
  },
  {
    key: 'stopwatch',
    path: '/sekundomer',
    includeInNav: true,
    seo: {
      path: '/sekundomer',
      title: 'Секундомер онлайн с миллисекундами — dateify.ru',
      description:
        'Секундомер онлайн с миллисекундами: старт и пауза пробелом, круги и сплиты, копирование результатов. Работает в браузере, бесплатно и без регистрации.',
      changefreq: 'monthly',
      priority: 0.8,
    },
  },
  {
    key: 'blog',
    path: '/blog',
    includeInNav: true,
    seo: {
      path: '/blog',
      title: 'Блог dateify - заметки про даты и время',
      description:
        'Практические заметки про расчёт дат, Unix timestamp, дедлайны, рабочие дни и локальные date/time инструменты в браузере.',
      changefreq: 'weekly',
      priority: 0.8,
    },
  },
] satisfies readonly UtilityStaticRouteDefinition<RouteKey>[]

const appRegistry = {
  domain: branding.domain,
  staticRoutes: dateifyRouteDefinitions,
  tools: [],
} satisfies UtilityAppRegistry<RouteKey, never>

const rawSeoRoutes: UmbrellaSeoRoutes<RouteKey> = {
  domain: branding.domain,
  routes: Object.fromEntries(
    dateifyRouteDefinitions.map((route) => [route.key, route.seo]),
  ) as UmbrellaSeoRoutes<RouteKey>['routes'],
}

export const seoRoutes: UmbrellaSeoRoutes<RouteKey> = {
  ...withDefaultJsonLd(rawSeoRoutes, 'This static route has no source-owned structured data.'),
  routes: Object.fromEntries(Object.entries(rawSeoRoutes.routes).map(([key, route]) => [
    key,
    {
      ...route,
      jsonLd: dateifyJsonLd(key as RouteKey, route, ruTranslations),
    },
  ])) as UmbrellaSeoRoutes<RouteKey>['routes'],
}

export const routePath = buildUtilityRoutePathMap(appRegistry)

export const resolveDateifyRoute = createUtilityRouteResolver<RouteKey>({
  routes: routePath,
  fallbackKey: 'index',
  dynamicRoutes: [{ key: 'blog', prefix: '/blog' }],
})

export function seoFor(key: RouteKey, locale: Locale = DEFAULT_LOCALE): SeoProps {
  if (isProductionCalendarRoute(key)) {
    return toSeoProps(seoRoutes.domain, seoRoutes.routes[key], { defaultImage: branding.ogImage })
  }

  if (locale === 'en') {
    const route = seoRoutes.routes[key]
    const seoKey = key === 'date-difference'
      ? 'dateDifference'
      : key === 'add-subtract-date'
        ? 'addSubtractDate'
      : key === 'business-days'
        ? 'businessDays'
      : key === 'age-calculator'
        ? 'ageCalculator'
        : key === 'days-until-date'
          ? 'daysUntilDate'
          : key === 'week-number'
            ? 'weekNumber'
            : key === 'time-zones'
              ? 'timeZones'
            : key === 'shift-calendar'
              ? 'shiftCalendar'
            : key === 'pomodoro'
              ? 'pomodoro'
            : key === 'calendar-2026'
              ? 'calendar2026'
              : key === 'calendar-2027'
                ? 'calendar2027'
                : key
    const localizedRoute = {
      ...route,
      ...enTranslations.seo[seoKey],
    }
    return toSeoProps(seoRoutes.domain, {
      ...localizedRoute,
      jsonLd: dateifyJsonLd(key, localizedRoute, enTranslations),
    }, { locale: 'en', defaultImage: branding.ogImage })
  }

  return toSeoProps(seoRoutes.domain, seoRoutes.routes[key], { defaultImage: branding.ogImage })
}

function dateifyJsonLd(
  key: RouteKey,
  route: SeoRouteConfig,
  translations: Translations,
): JsonLdExpectation {
  if (key === 'index' || key === 'blog') {
    return { mode: 'none', reason: 'This index route does not represent an individual software tool.' }
  }

  if (isProductionCalendarRoute(key)) {
    return productionCalendarJsonLd(key, route)
  }

  const canonical = `https://${branding.domain}${route.path}`
  const content = dateifySchemaContent(key, translations)
  const schemas: object[] = [
    buildBreadcrumbSchema([
      { name: branding.domain, url: `https://${branding.domain}/` },
      { name: route.title, url: canonical },
    ]),
    isCalendarRoute(key)
      ? buildCalendarWebApplicationSchema(route.title, route.description, canonical)
      : buildSoftwareApplicationSchema({
        name: route.title,
        description: route.description,
        category: 'UtilitiesApplication',
        url: canonical,
      }),
  ]
  if (!isCalendarRoute(key)) schemas.push(buildHowToSchema(route.title, content.howTo))
  schemas.push(buildFaqSchema(content.faq))
  return { mode: 'required', schemas }
}

function dateifySchemaContent(key: RouteKey, t: Translations): {
  howTo: readonly string[]
  faq: readonly { question: string; answer: string }[]
} {
  if (key === 'calendar' || key === 'calendar-2026' || key === 'calendar-2027') {
    const year = key === 'calendar-2026' ? '2026' : key === 'calendar-2027' ? '2027' : undefined
    return {
      howTo: [],
      faq: year
        ? t.calendar.yearFaq.map((item) => ({
          question: item.question.replace('{year}', year),
          answer: item.answer.replace('{year}', year),
        }))
        : t.calendar.faq,
    }
  }
  if (key === 'timestamp') {
    return {
      howTo: [
        `${t.timestamp.timestampToDate}: ${t.timestamp.inputPlaceholder}.`,
        `${t.timestamp.dateToTimestamp}: ${t.timestamp.dateTime} + ${t.timestamp.timezone}.`,
        `${t.common.copy}: ${[t.timestamp.seconds, t.timestamp.milliseconds, t.timestamp.nanoseconds, t.timestamp.epochDays].join(' / ')}.`,
      ],
      faq: t.timestamp.faq.map((item) => ({ question: item.title, answer: item.body })),
    }
  }
  const content = key === 'add-subtract-date' ? t.addSubtractDate
    : key === 'date-difference' ? t.dateDifference
      : key === 'business-days' ? t.businessDays
        : key === 'age-calculator' ? t.ageCalculator
          : key === 'days-until-date' ? t.daysUntilDate
            : key === 'week-number' ? t.weekNumber
              : key === 'time-zones' ? t.timeZones
                : key === 'shift-calendar' ? t.shiftCalendar
                : key === 'timer' ? t.timer
                  : key === 'pomodoro' ? t.pomodoro
                    : t.stopwatch
  return { howTo: content.howTo, faq: content.faq }
}

function isCalendarRoute(key: RouteKey): boolean {
  return key === 'calendar' || key === 'calendar-2026' || key === 'calendar-2027'
}

function isProductionCalendarRoute(key: RouteKey): key is 'production-calendar' | ProductionCalendarYearRouteKey {
  return key === 'production-calendar' || key.startsWith('production-calendar-')
}

function productionCalendarJsonLd(
  key: 'production-calendar' | ProductionCalendarYearRouteKey,
  route: SeoRouteConfig,
): JsonLdExpectation {
  const canonical = `https://${branding.domain}${route.path}`
  const breadcrumbs = key === 'production-calendar'
    ? [
      { name: branding.domain, url: `https://${branding.domain}/` },
      { name: 'Производственный календарь', url: canonical },
    ]
    : [
      { name: branding.domain, url: `https://${branding.domain}/` },
      { name: 'Производственный календарь', url: `https://${branding.domain}/proizvodstvennyj-kalendar` },
      { name: route.title, url: canonical },
    ]
  const schemas: object[] = [
    buildBreadcrumbSchema(breadcrumbs),
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: route.title,
      description: route.description,
      url: canonical,
    },
  ]

  if (key === 'production-calendar') {
    schemas.push(buildFaqSchema([
      {
        question: 'Это официальный календарь?',
        answer: 'Нет. Календарь dateify составлен на основе официально опубликованных актов и имеет справочный характер.',
      },
      {
        question: 'Почему доступны не все годы?',
        answer: 'Год появляется только после публикации акта о переносах, второй независимой сверки и перевода набора данных в approved.',
      },
      {
        question: 'Подойдёт ли он для моего графика?',
        answer: 'Только для федеральной пятидневки. Региональные праздники, шестидневка, сменный и индивидуальный график могут дать другой результат.',
      },
    ]))
  }

  return { mode: 'required', schemas }
}

function buildCalendarWebApplicationSchema(name: string, description: string, url: string): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name,
    description,
    applicationCategory: 'UtilitiesApplication',
    url,
    operatingSystem: 'Web',
    offers: { '@type': 'Offer', price: 0, priceCurrency: 'RUB' },
  }
}
